<script setup lang="ts">
import { computed, defineProps, onMounted, onUnmounted, ref } from 'vue';
import { showNotification } from '../../../notifications/notification_actions';

const props = defineProps<{
  width: number;
  height: number;
  originalWidth: number;
  originalHeight: number;
}>();

const scroll = ref<HTMLDivElement | null>(null);

let isDragging = false;
let lastX = 0;
let lastY = 0;

const zoom = ref<number>(1);
const size = computed(() => {
  return {
    width: props.width * zoom.value,
    height: props.height * zoom.value,
  };
});
const labelZoom = computed(() => size.value.width / props.originalWidth);

function documentKeydown(event: KeyboardEvent) {
  if (event.metaKey && (event.key === '=' || event.key === '+')) {
    event.preventDefault();
    if (labelZoom.value > 4) return;
    zoom.value += 0.1;
  } else if (event.metaKey && event.key === '-') {
    event.preventDefault();
    if (labelZoom.value < 0.2) return;
    zoom.value -= 0.1;
  } else if (event.metaKey && event.key === '0') {
    event.preventDefault();
    zoom.value = 1;
  }

  if (event.metaKey && (event.key === '=' || event.key === '+' || event.key === '-' || event.key === '0')) {
    event.preventDefault();

    const percent = Math.round(labelZoom.value * 100);
    showNotification('fast_type', {
      title: `Zoom ${percent}%`,
    });
  }

  if (event.code === 'Space') {
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
      return;
    }

    event.preventDefault();
    scroll.value?.classList.add('hand-tool');
  }
}

function documentKeyup(event: KeyboardEvent) {
  if (event.code === 'Space') {
    scroll.value?.classList.remove('hand-tool');
    isDragging = false;
  }
}

function handleWheel(event: WheelEvent) {
  // zoom only with ⌘ (macOS) or Ctrl (Windows)
  if (!event.metaKey && !event.ctrlKey) return;

  // block native zoom
  event.preventDefault();

  const zoomContainer = scroll.value;
  if (!zoomContainer) return;
  const rect = zoomContainer.getBoundingClientRect();
  const mouseX = event.clientX - rect.left;
  const mouseY = event.clientY - rect.top;

  const oldScale = zoom.value;
  const delta = event.deltaY > 0 ? -0.1 : 0.1;
  const newScale = Math.min(Math.max(oldScale + delta, 0.3), 5);

  if (newScale === oldScale) return;

  const scaleRatio = newScale / oldScale;

  // Keep point under cursor fixed
  const scrollLeft = (mouseX + zoomContainer.scrollLeft) * scaleRatio - mouseX;
  const scrollTop = (mouseY + zoomContainer.scrollTop) * scaleRatio - mouseY;

  zoom.value = newScale;

  zoomContainer.scrollLeft = scrollLeft;
  zoomContainer.scrollTop = scrollTop;

  const percent = Math.round(labelZoom.value * 100);
  showNotification('fast_type', {
    title: `Zoom ${percent}%`,
  });
}

function onMouseDown(e: MouseEvent) {
  if (!scroll.value?.classList.contains('hand-tool')) return;

  e.preventDefault();
  isDragging = true;
  lastX = e.clientX;
  lastY = e.clientY;
}

function onMouseMove(e: MouseEvent) {
  if (!isDragging) return;

  const dx = e.clientX - lastX;
  const dy = e.clientY - lastY;

  lastX = e.clientX;
  lastY = e.clientY;

  if (!scroll.value) return;
  scroll.value.scrollLeft -= dx;
  scroll.value.scrollTop -= dy;
}

function onMouseUp() {
  isDragging = false;
}

onMounted(() => {
  document.addEventListener('keydown', documentKeydown);
  document.addEventListener('keyup', documentKeyup);
  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
});

onUnmounted(() => {
  document.removeEventListener('keydown', documentKeydown);
  document.removeEventListener('keyup', documentKeyup);
  document.removeEventListener('mousemove', onMouseMove);
  document.removeEventListener('mouseup', onMouseUp);
});
</script>

<template>
  <div
    id="draft-zoom-container"
    ref="scroll"
    class="flex h-screen w-screen items-center justify-center overflow-auto"
    @wheel="handleWheel"
    @mousedown="onMouseDown"
  >
    <div class="max-h-screen" style="max-width: 100vw">
      <div :style="'height:' + size.height + 'px; width:' + size.width + 'px;'" class="relative">
        <slot :zoom="zoom"></slot>
      </div>
    </div>
  </div>
</template>

<style>
#draft-zoom-container.hand-tool {
  cursor: grab;
}

#draft-zoom-container.hand-tool:active {
  cursor: grabbing;
}
</style>
